import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithActivity } from '../utils/fetchWithActivity';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface CombinedSource {
  id: string;
  name: string;
  uploadedAt: string;
}

interface CombinedDocumentChipProps {
  documentId: string;
  /** Called after a successful uncombine so the caller can refresh its document list. */
  onUncombined?: () => void;
}

// Combined-document receipt chip (E5) + Uncombine (E6). Neutral gray — a
// different concept from the green DocumentIntelligenceChip (extraction
// classification vs. automation transparency).
const CombinedDocumentChip: React.FC<CombinedDocumentChipProps> = ({ documentId, onUncombined }) => {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sources, setSources] = useState<CombinedSource[] | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [uncombining, setUncombining] = useState(false);
  const [blockedReportTitles, setBlockedReportTitles] = useState<string[] | null>(null);

  const fetchSources = async () => {
    setLoading(true);
    try {
      const res = await fetchWithActivity(`${API_BASE_URL}/api/document-combination/${documentId}/sources`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setSources(await res.json());
    } finally {
      setLoading(false);
    }
  };

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next && sources === null) fetchSources();
  };

  const handleUncombine = async () => {
    setUncombining(true);
    setBlockedReportTitles(null);
    try {
      const res = await fetchWithActivity(`${API_BASE_URL}/api/document-combination/${documentId}/uncombine`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setOpen(false);
        onUncombined?.();
      } else if (res.status === 409) {
        const data = await res.json().catch(() => ({}));
        setBlockedReportTitles(data.reportTitles || []);
      }
    } finally {
      setUncombining(false);
      setConfirming(false);
    }
  };

  const sourceCount = sources?.length ?? 0;

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          toggleOpen();
        }}
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
      >
        Combined{sourceCount > 0 ? ` from ${sourceCount} files` : ''}
      </button>

      {open && (
        <div
          className="absolute z-50 top-full left-0 mt-2 w-72 p-3 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {loading ? (
            <p className="text-xs text-accent dark:text-dark-muted">Loading sources…</p>
          ) : sources !== null && sources.length === 0 ? (
            <p className="text-xs text-secondary dark:text-dark-text">
              This document was combined before source tracking existed — the original file list isn't available. To remove it, delete the document normally.
            </p>
          ) : (
            <>
              <p className="text-xs font-medium text-secondary dark:text-dark-text mb-2">
                Combined from {sourceCount} document{sourceCount === 1 ? '' : 's'}
              </p>
              <ul className="space-y-1 mb-3 max-h-40 overflow-y-auto">
                {sources?.map((s) => (
                  <li key={s.id} className="text-xs text-accent dark:text-dark-muted truncate" title={s.name}>
                    {s.name} — {new Date(s.uploadedAt).toLocaleDateString()}
                  </li>
                ))}
              </ul>

              {blockedReportTitles && blockedReportTitles.length > 0 && (
                <p className="text-xs text-red-600 dark:text-red-400 mb-2">
                  Can't uncombine — used by report{blockedReportTitles.length === 1 ? '' : 's'}: {blockedReportTitles.join(', ')}
                </p>
              )}

              {confirming && (
                <p className="text-xs text-secondary dark:text-dark-text mb-2">
                  This deletes the combined file and restores the {sourceCount} original document{sourceCount === 1 ? '' : 's'}. Reports already generated from it are unaffected.
                </p>
              )}

              <div className="flex justify-end gap-2">
                {confirming ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setConfirming(false)}
                      className="px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleUncombine}
                      disabled={uncombining}
                      className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      {uncombining ? 'Uncombining…' : 'Yes, uncombine'}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirming(true)}
                    className="px-2 py-1 text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                  >
                    Uncombine
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CombinedDocumentChip;
